import { useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { format, parseISO } from "date-fns";
import { ArrowLeft, Pencil, Play, Send, XCircle } from "lucide-react";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorBoundary } from "@/components/shared/error-boundary";
import { StatusDot } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useProcessPlan, useUpdateProcessPlanStatus } from "@/features/planning/api";
import {
  INPUT_SOURCES,
  PLAN_STATUSES,
  canEditPlan,
  labelFor,
  statusTone,
} from "@/features/planning/constants";
import type { ProcessPlanStage, ProcessPlanStatus } from "@/features/planning/types";
import { ExecuteStageSheet } from "@/features/production/execute-stage-sheet";

export function PlanningDetailPage({ planId }: { planId: string }) {
  return (
    <ErrorBoundary title="Plan detail crashed">
      <PlanningDetailInner planId={planId} />
    </ErrorBoundary>
  );
}

function PlanningDetailInner({ planId }: { planId: string }) {
  const navigate = useNavigate();
  const { data: plan, isLoading, error } = useProcessPlan(planId);
  const updateStatus = useUpdateProcessPlanStatus();
  const [runStage, setRunStage] = useState<ProcessPlanStage | null>(null);

  if (isLoading) {
    return <p className="py-16 text-center text-sm text-muted-foreground">Loading…</p>;
  }
  if (error || !plan) {
    return (
      <EmptyState
        title="Plan not found"
        description={(error as Error | undefined)?.message ?? "This plan does not exist."}
        action={
          <Button asChild size="sm" variant="outline">
            <Link to="/planning">Back</Link>
          </Button>
        }
      />
    );
  }

  function setStatus(status: ProcessPlanStatus) {
    updateStatus.mutate({ id: planId, status });
  }

  return (
    <div className="mx-auto max-w-5xl space-y-3 pb-8">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <Button type="button" variant="ghost" size="sm" className="mb-1 -ml-2" asChild>
            <Link to="/planning">
              <ArrowLeft />
              Plans
            </Link>
          </Button>
          <h1 className="font-mono text-lg font-semibold tracking-tight">{plan.plan_no}</h1>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <StatusDot tone={statusTone(plan.status)}>
              {labelFor(PLAN_STATUSES, plan.status)}
            </StatusDot>
            <span>{format(parseISO(plan.plan_date), "dd MMM yyyy")}</span>
            {plan.so_number ? (
              <span>
                {plan.so_number}
                {plan.client_name ? ` · ${plan.client_name}` : ""}
              </span>
            ) : null}
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {canEditPlan(plan.status) ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => navigate({ to: "/planning/$planId/edit", params: { planId } })}
            >
              <Pencil />
              Edit
            </Button>
          ) : null}
          {plan.status === "draft" ? (
            <Button
              type="button"
              size="sm"
              disabled={updateStatus.isPending}
              onClick={() => setStatus("planned")}
            >
              <Send />
              Mark planned
            </Button>
          ) : null}
          {plan.status === "draft" || plan.status === "planned" ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={updateStatus.isPending}
              onClick={() => setStatus("cancelled")}
            >
              <XCircle />
              Cancel
            </Button>
          ) : null}
        </div>
      </div>

      {plan.notes ? (
        <p className="rounded-md border border-border bg-muted/30 px-3 py-2 text-xs">{plan.notes}</p>
      ) : null}

      {(plan.stages ?? []).some((s) => s.quality_check_required && s.status === "completed") ? (
        <p className="rounded-md border border-border bg-card px-3 py-2 text-xs text-muted-foreground">
          QC-required stages produce pending batches.{" "}
          <Link to="/quality" className="font-medium text-primary hover:underline">
            Open quality queue
          </Link>
        </p>
      ) : null}

      {(plan.stages ?? []).map((stage) => (
        <section key={stage.id} className="rounded-md border border-border bg-card">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-3 py-2">
            <div>
              <div className="text-xs font-semibold">
                Stage {stage.stage_index} · {stage.process_name}
                <span className="ml-2 font-normal text-muted-foreground">({stage.status})</span>
              </div>
              <div className="text-2xs text-muted-foreground">
                {stage.machine_code} — {stage.machine_name} ·{" "}
                {labelFor(INPUT_SOURCES, stage.input_source)}
                {stage.quality_check_required ? " · QC required" : ""}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="text-2xs text-muted-foreground tabular-nums">
                {stage.planned_start_at
                  ? format(parseISO(stage.planned_start_at), "dd MMM")
                  : "—"}
                {" → "}
                {stage.planned_end_at ? format(parseISO(stage.planned_end_at), "dd MMM") : "—"}
              </div>
              {stage.status === "planned" &&
              ["planned", "in_progress"].includes(plan.status) ? (
                <Button type="button" size="sm" onClick={() => setRunStage(stage)}>
                  <Play />
                  Run
                </Button>
              ) : null}
            </div>
          </div>

          {stage.input_source === "inventory" ? (
            <div className="px-3 py-2">
              <div className="mb-1 text-2xs font-semibold uppercase tracking-wide text-muted-foreground">
                Inputs
              </div>
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead>Batch</TableHead>
                    <TableHead>Material</TableHead>
                    <TableHead className="text-right">Qty</TableHead>
                    <TableHead className="text-right">Weight</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(stage.inputs ?? []).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-muted-foreground">
                        No inputs
                      </TableCell>
                    </TableRow>
                  ) : (
                    (stage.inputs ?? []).map((inp) => (
                      <TableRow key={inp.id}>
                        <TableCell className="font-mono text-xs">{inp.batch_no}</TableCell>
                        <TableCell className="text-xs">{inp.material_name || "—"}</TableCell>
                        <TableCell className="text-right tabular-nums">{inp.planned_qty}</TableCell>
                        <TableCell className="text-right tabular-nums">{inp.planned_weight}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          ) : (
            <p className="px-3 py-2 text-2xs text-muted-foreground">
              Input from previous stage output (execution in 6.3).
            </p>
          )}

          <div className="border-t border-border px-3 py-2">
            <div className="mb-1 text-2xs font-semibold uppercase tracking-wide text-muted-foreground">
              Outputs
            </div>
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Material</TableHead>
                  <TableHead>Grade / Size</TableHead>
                  <TableHead>Dims</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead className="text-right">Weight</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(stage.outputs ?? []).map((out) => (
                  <TableRow key={out.id}>
                    <TableCell className="text-xs font-medium">{out.material_name}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {[out.grade_name, out.shape_label].filter(Boolean).join(" · ") || "—"}
                    </TableCell>
                    <TableCell className="text-2xs tabular-nums text-muted-foreground">
                      {[
                        out.thickness_mm != null ? `T${out.thickness_mm}` : null,
                        out.width_mm != null ? `W${out.width_mm}` : null,
                        out.length_mm != null ? `L${out.length_mm}` : null,
                      ]
                        .filter(Boolean)
                        .join(" ") || "—"}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{out.planned_qty}</TableCell>
                    <TableCell className="text-right tabular-nums">{out.planned_weight}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </section>
      ))}

      <ExecuteStageSheet
        open={Boolean(runStage)}
        onOpenChange={(o) => {
          if (!o) setRunStage(null);
        }}
        stage={runStage}
      />
    </div>
  );
}
